/**
 * ==============================================
 * SECURE API UTILITY - CENTRALIZED CONFIGURATION
 * ==============================================
 *
 * This utility handles ALL API requests with:
 * ✅ Automatic JWT token handling via httpOnly cookies
 * ✅ CSRF protection
 * ✅ Request/response interceptors
 * ✅ Automatic token refresh
 * ✅ Error handling with auto-logout on 401
 *
 * SECURITY FEATURES:
 * - Tokens stored in httpOnly cookies (NOT localStorage!)
 * - Automatic token refresh before expiration
 * - Request timeout protection
 * - XSS prevention (no JS access to tokens)
 * - CSRF token validation
 */

import axios from 'axios';
import toast from 'react-hot-toast';

// ==================== CONFIGURATION ====================
// ✅ Using Create React App env variables (REACT_APP_*)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://domains-marketplace-backend.vercel.app';
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;

// ==================== CREATE AXIOS INSTANCE ====================
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  withCredentials: true, // ✅ CRITICAL: Enables httpOnly cookie handling
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // CSRF protection
  },
});

// ==================== REQUEST INTERCEPTOR ====================
/**
 * Automatically attaches CSRF token and handles authentication
 */
api.interceptors.request.use(
  (config) => {
    // Add CSRF token from meta tag if available
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ==================== RESPONSE INTERCEPTOR ====================
/**
 * Handles token refresh and automatic logout on 401
 */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // ✅ IMPORTANT: Don't try to refresh tokens for login/register/public endpoints
    const isAuthEndpoint =
      originalRequest.url?.includes('/authenticate') ||
      originalRequest.url?.includes('/login') ||
      originalRequest.url?.includes('/register') ||
      originalRequest.url?.includes('/forgot-password') ||
      originalRequest.url?.includes('/reset-password') ||
      originalRequest.url?.includes('/refresh-token');

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue requests while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        await api.post('/sam/doc/users/refresh-token');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Token refresh failed - logout user
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      toast.error('Access denied. You do not have permission for this action.');
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      toast.error('Resource not found.');
    }

    // Handle 500 Server Error
    if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    }

    // Handle network errors
    if (!error.response) {
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

// ==================== LOGOUT HANDLER ====================
/**
 * Clears authentication state and redirects to login
 */
const handleLogout = () => {
  // Clear any client-side state (don't use localStorage for tokens!)
  localStorage.removeItem('isAuth');
  localStorage.removeItem('loginUser');
  localStorage.removeItem('user');
  sessionStorage.clear();

  // Show notification
  toast.error('Your session has expired. Please login again.');

  // Redirect to login after a short delay
  setTimeout(() => {
    window.location.href = '/login';
  }, 1500);
};

// ==================== HELPER FUNCTIONS ====================

/**
 * GET request
 */
export const get = async (url, config = {}) => {
  try {
    const response = await api.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * POST request
 */
export const post = async (url, data = {}, config = {}) => {
  try {
    const response = await api.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PUT request
 */
export const put = async (url, data = {}, config = {}) => {
  try {
    const response = await api.put(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * DELETE request
 */
export const del = async (url, config = {}) => {
  try {
    const response = await api.delete(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PATCH request
 */
export const patch = async (url, data = {}, config = {}) => {
  try {
    const response = await api.patch(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload file (multipart/form-data)
 */
export const uploadFile = async (url, formData, onUploadProgress = null) => {
  try {
    const response = await api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Download file
 */
export const downloadFile = async (url, filename) => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    });

    // Create download link
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return true;
  } catch (error) {
    throw error;
  }
};

// ==================== RETRY LOGIC ====================
/**
 * Retry a request with exponential backoff
 */
export const retryRequest = async (requestFn, maxRetries = MAX_RETRIES) => {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      // Don't retry on 4xx errors (client errors)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      if (i < maxRetries - 1) {
        const delay = Math.min(1000 * Math.pow(2, i), 10000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

// ==================== LEGACY COMPATIBILITY ====================
// Keep these for backward compatibility with existing code

/**
 * @deprecated Use withCredentials axios instance instead
 * Makes an authenticated fetch request
 */
export const authenticatedFetch = async (url, options = {}) => {
  try {
    const method = (options.method || 'GET').toLowerCase();
    const config = {
      ...options,
      headers: options.headers || {},
    };

    // Remove method from config as axios uses separate methods
    delete config.method;

    let response;
    if (method === 'get' || method === 'delete') {
      response = await api[method](url, config);
    } else {
      response = await api[method](url, options.body, config);
    }

    // Return response compatible with fetch API
    return {
      ok: true,
      status: response.status,
      json: async () => response.data,
      ...response,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * @deprecated Tokens are now in httpOnly cookies, not accessible to JS
 * Gets the current authentication token
 */
export const getAuthToken = () => {
  return null;
};

/**
 * @deprecated Use server-side session validation instead
 * Checks if user is authenticated
 */
export const isAuthenticated = () => {
  const loginUser = localStorage.getItem('loginUser');
  return !!loginUser;
};

/**
 * @deprecated Tokens are now managed server-side via httpOnly cookies
 * Saves authentication token
 */
export const saveAuthToken = (token, rememberMe = true) => {
  // No-op for security
};

/**
 * @deprecated Tokens are in httpOnly cookies, cleared by server on logout
 * Clears authentication token
 */
export const clearAuthToken = () => {
  // No-op for security
};

// ==================== EXPORTS ====================
export default api;

export {
  API_BASE_URL,
  handleLogout,
};
